// Accounts "Add account" modal, first step: Connect a bank (Plaid Link, with
// bank_feeds.manage on the one selected business) or Add manually. After a
// link: pick, per bank account, a new eloNest account (default), an existing
// one, or don't import; then the existing sync imports rows with its dedup
// and derives the starting balance from the bank (no double counting).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { bankFeedsAPI } from './bankFeedsApi';
import ConnectBankButton, { connectAllowed } from './ConnectBankButton';
import MappingSelect, { mappingPayload, defaultChoice } from './MappingSelect';
import { activeBusinessIds } from '../../utils/businessMulti';
import { formatApiError } from '../../utils/errorMessage';

export const singleBusinessId = (selected) => (/^\d{1,10}$/.test(String(selected ?? '')) ? Number(selected) : null);

// 'connect' | 'pick-one' (has manage somewhere but several/All selected) | 'manual'
export function setupOffer({ businessId, status, multiCount = 0 }) {
  if (!status || !status.configured) return 'manual';
  const p = status.permissions;
  if (businessId && multiCount < 2) return connectAllowed({ businessId, permissions: p }) ? 'connect' : 'manual';
  return (p?.manage_business_ids || []).length ? 'pick-one' : 'manual';
}

export function importSummary(d) {
  if (!d || d.needs_reconnect) return 'The bank needs to be reconnected before importing.';
  return `Imported ${d.added || 0} transaction${d.added === 1 ? '' : 's'}${d.linked ? `, ${d.linked} matched to rows already in eloNest` : ''}${d.match_review ? `, ${d.match_review} possible duplicate${d.match_review === 1 ? '' : 's'} to review in Bank Feeds` : ''}.`;
}

export default function BankLinkSetup({ selectedBusiness, accounts, onManual, onDone, onCancel }) {
  const businessId = singleBusinessId(selectedBusiness);
  const [offer, setOffer] = useState(null);
  const [item, setItem] = useState(null);
  const [choice, setChoice] = useState({});
  const [hints, setHints] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    let live = true;
    bankFeedsAPI.status()
      .then((s) => { if (live) setOffer(setupOffer({ businessId, status: s.data, multiCount: activeBusinessIds().length })); })
      .catch(() => { if (live) setOffer('manual'); });
    return () => { live = false; };
  }, [businessId]);
  useEffect(() => { if (offer === 'manual') onManual(); }, [offer, onManual]);

  const linked = async (newItem) => {
    if (!newItem) return;
    const all = (await bankFeedsAPI.items()).data.items || [];
    const full = all.find((i) => i.id === newItem.id) || { ...newItem, accounts: [] };
    // Suggestions cover every business the user manages Bank Feeds for; without
    // them the picker falls back to this business only.
    const s = await Promise.resolve().then(() => bankFeedsAPI.suggestions(full.id, businessId)).then((r) => r.data).catch(() => null);
    const byId = Object.fromEntries((s?.accounts || []).map((x) => [x.id, x]));
    setHints(s ? { groups: s.groups, byId } : null);
    setChoice(Object.fromEntries(full.accounts.map((pa) => [pa.id, defaultChoice(byId[pa.id], full.business_id, s?.groups, businessId)])));
    setItem(full);
  };

  const confirm = async () => {
    setBusy(true); setError('');
    try {
      for (const pa of item.accounts) await bankFeedsAPI.mapAccount(pa.id, mappingPayload(choice[pa.id] || 'ignore'));
      const r = await bankFeedsAPI.sync(item.id);
      setDone(importSummary(r.data));
    } catch (e) { setError(formatApiError(e, 'Could not finish setting up the bank accounts')); }
    setBusy(false);
  };

  if (offer === null || offer === 'manual') return <p className="text-sm text-gray-600">Loading…</p>;
  const btn = 'px-4 py-2 min-h-[44px] rounded text-sm';
  if (done) {
    return (
      <div className="space-y-3" role="status">
        <p>{done}</p>
        <div className="flex flex-wrap gap-2 justify-end">
          <Link to="/bank-feeds" className={`${btn} border inline-flex items-center`}>Open Bank Feeds</Link>
          <button type="button" className={`${btn} bg-blue-600 text-white`} onClick={onDone}>Done</button>
        </div>
      </div>
    );
  }
  if (item) {
    return (
      <div className="space-y-3">
        <p className="text-sm">Bank connected{item.institution_name ? ` (${item.institution_name})` : ''}. Choose where each bank account goes:</p>
        {item.accounts.map((pa) => (
          <div key={pa.id} className="border rounded p-2 text-sm">
            <div className="font-medium">{pa.name}{pa.mask ? ` ••${pa.mask}` : ''} <span className="text-gray-500">({pa.plaid_label || pa.plaid_subtype || pa.plaid_type})</span></div>
            <MappingSelect pa={pa} accounts={accounts} groups={hints?.groups} suggestion={hints?.byId[pa.id]} businessId={businessId}
              value={String(choice[pa.id] || 'create')} allowEmpty={false}
              onChange={(v) => setChoice({ ...choice, [pa.id]: v })} disabled={busy} />
          </div>
        ))}
        {hints && <p className="text-xs text-gray-500">Accounts with the same last 4 digits are pre-selected, in whichever business they belong to. Check each one before confirming.</p>}
        <p className="text-xs text-gray-500">New accounts take their name and type from the bank. The first import sets the starting balance from the bank, so nothing is counted twice.</p>
        {error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
        <div className="flex flex-wrap gap-2 justify-end">
          <button type="button" className={`${btn} border`} onClick={onDone} disabled={busy}>Later (map in Bank Feeds)</button>
          <button type="button" className={`${btn} bg-blue-600 text-white`} onClick={confirm} disabled={busy}>{busy ? 'Importing…' : 'Confirm and import'}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {offer === 'connect'
          ? <ConnectBankButton businessId={businessId} onItem={linked} onConnected={() => {}} onError={setError} className={`${btn} bg-blue-600 text-white`} />
          : <button type="button" className={`${btn} border`} disabled title="Pick one business in the selector to connect a bank">Connect a bank</button>}
        <button type="button" className={`${btn} border`} onClick={onManual}>Add manually</button>
      </div>
      {offer === 'pick-one' && <p className="text-xs text-gray-500">Pick one business in the selector to connect a bank.</p>}
      {error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
      <div className="flex justify-end"><button type="button" className={`${btn} border`} onClick={onCancel}>Cancel</button></div>
    </div>
  );
}
